import { useEffect, useRef, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePwaInstall, isStandalone, isIos } from "@/hooks/usePwaInstall";

const STORAGE_KEY = "sp_install_prompt_dismissed_v1";
const AUTO_HIDE_MS = 9000;

const InstallAppBanner = () => {
  const { canPromptInstall, promptInstall } = usePwaInstall();
  const [showIosModal, setShowIosModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const autoHideRef = useRef<number | undefined>(undefined);

  const clearAutoHide = () => {
    if (autoHideRef.current) {
      window.clearTimeout(autoHideRef.current);
      autoHideRef.current = undefined;
    }
  };

  const scheduleAutoHide = () => {
    clearAutoHide();
    autoHideRef.current = window.setTimeout(() => setVisible(false), AUTO_HIDE_MS);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const previewMode = params.get("previewInstall") === "1";
    if (previewMode) {
      setVisible(true);
      setShowIosModal(true);
      return;
    }
    if (isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    let iosTimer: number | undefined;
    if (isIos()) {
      iosTimer = window.setTimeout(() => setVisible(true), 4000);
    }
    return () => {
      if (iosTimer) window.clearTimeout(iosTimer);
      clearAutoHide();
    };
  }, []);

  // Show banner when an install prompt becomes available (Android/Chrome).
  useEffect(() => {
    if (!canPromptInstall) return;
    if (isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;
    setVisible(true);
  }, [canPromptInstall]);

  // Auto-hide the banner after a few seconds without interaction. Closing
  // manually still records the preference in localStorage (see dismiss).
  useEffect(() => {
    if (visible) scheduleAutoHide();
    else clearAutoHide();
    return clearAutoHide;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    clearAutoHide();
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  const handleInstall = async () => {
    clearAutoHide();
    if (canPromptInstall) {
      const result = await promptInstall();
      if (result !== "unavailable") {
        localStorage.setItem(STORAGE_KEY, "1");
      }
      setVisible(false);
    } else if (isIos()) {
      setShowIosModal(true);
    }
  };

  if (!visible && !showIosModal) return null;

  return (
    <>
      {visible && (
        <div
          className="fixed bottom-20 right-4 z-50 md:bottom-6 md:right-6 animate-in fade-in slide-in-from-bottom-4"
          role="region"
          aria-label="Instalar aplicación"
        >
          <div className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg pl-4 pr-2 py-2">
            <button
              type="button"
              onClick={handleInstall}
              className="flex items-center gap-2 text-sm font-medium"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Instalar app
            </button>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Cerrar"
              className="rounded-full p-1.5 hover:bg-white/15 transition-colors"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={showIosModal} onOpenChange={(open) => { setShowIosModal(open); if (!open) dismiss(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Instalar Sana Pilates</DialogTitle>
            <DialogDescription>
              Para agregar la app a tu pantalla de inicio en iPhone o iPad:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-foreground">
            <li className="flex items-start gap-2">
              <span className="font-semibold">1.</span>
              <span>
                Tocá el botón <Share className="inline h-4 w-4 align-text-bottom" aria-label="Compartir" /> <strong>Compartir</strong> en la barra de Safari.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold">2.</span>
              <span>Deslizá y elegí <strong>"Agregar a pantalla de inicio"</strong>.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold">3.</span>
              <span>Tocá <strong>"Agregar"</strong> arriba a la derecha.</span>
            </li>
          </ol>
          <Button onClick={() => { setShowIosModal(false); dismiss(); }} className="w-full h-12">
            Entendido
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppBanner;